// Service for the Substitution Analyst feature.
//
// Two backends are involved, exactly like the rest of the "AI Agents" group:
//   - The Python Process Optimization agent does the trace/compute (RACI -> job ->
//     FTE cascade, redeployment scoring, narrative). Slow / AI-adjacent work, so it
//     runs as an async job: POST .../start returns a job_id immediately, poll
//     GET .../jobs/{id} until status is "done" or "error" — same shape as
//     processOptimizationService.js's pollJob().
//   - The main CMS (Nest) backend persists saved analyses (draft / override /
//     confirm), the same propose-then-confirm posture as valueAnalysisService.js.
//
// The two sections below are kept separate on purpose: they hit different origins
// with different auth (Bearer token vs. cookies).
import api from "./api";

/* ------------------------------------------------------------------------ */
/* Section A — Python agent (compute)                                       */
/* ------------------------------------------------------------------------ */

const AGENT_BASE_URL =
  process.env.REACT_APP_PROCESS_OPT_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  "";

function buildAgentHeaders(extra = {}) {
  let token = null;
  try {
    const raw = sessionStorage.getItem("auth");
    if (raw) token = JSON.parse(raw)?.accessToken || null;
  } catch (_) { }
  return {
    Accept: "application/json,*/*",
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .then((res) => { clearTimeout(timer); return res; })
    .catch((err) => {
      clearTimeout(timer);
      if (err.name === "AbortError") throw new Error(`Request timed out after ${timeoutMs / 1000}s`);
      throw err;
    });
}

async function agentPostJson(path, body, timeoutMs = 30_000) {
  const url = `${AGENT_BASE_URL}${path}`;
  const res = await fetchWithTimeout(
    url,
    { method: "POST", headers: buildAgentHeaders(), body: JSON.stringify(body) },
    timeoutMs,
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function agentGetJson(path, timeoutMs = 30_000) {
  const url = `${AGENT_BASE_URL}${path}`;
  const res = await fetchWithTimeout(url, { method: "GET", headers: buildAgentHeaders() }, timeoutMs);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

// Poll GET /cms/substitution-analyst/jobs/{job_id} every 4s until status is
// "done" or "error". Max 75 attempts (~5 minutes), matching processOptimizationService.js.
async function pollSubstitutionJob(jobId, intervalMs = 4000) {
  const MAX_POLLS = 75;
  for (let i = 0; i < MAX_POLLS; i++) {
    const data = await agentGetJson(`/cms/substitution-analyst/jobs/${encodeURIComponent(jobId)}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") throw new Error(data.error || "Substitution trace failed on server");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Substitution trace timed out after 5 minutes. Please try again.");
}

/**
 * Run a substitution trace end-to-end: start the async job, then poll until done.
 * @param {number} processId
 * @param {number[]} taskIds - automation-candidate task ids
 * @param {"R"|"RA"|"ALL"} raciScope - which RACI roles' time counts as freed
 */
export async function runSubstitutionTrace(processId, taskIds, raciScope) {
  if (!processId) throw new Error("processId is required");
  if (!Array.isArray(taskIds) || taskIds.length === 0) {
    throw new Error("At least one task must be selected");
  }
  const { job_id } = await agentPostJson("/cms/substitution-analyst/start", {
    process_id: processId,
    task_ids: taskIds,
    raci_scope: raciScope,
  });
  return pollSubstitutionJob(job_id);
}

/* ------------------------------------------------------------------------ */
/* Section B — Nest backend (persistence: draft / override / confirm)      */
/* ------------------------------------------------------------------------ */

/** List saved substitution analyses for a process. */
export async function getSubstitutionDrafts(processId) {
  if (!processId) throw new Error("processId is required");
  return api.get(`/process/${encodeURIComponent(processId)}/substitution-analyst`);
}

/** Save a new draft from an agent trace result. Nothing is authoritative until confirmed. */
export async function saveSubstitutionDraft(processId, { taskIds, raciScope, result }) {
  if (!processId) throw new Error("processId is required");
  return api.post(`/process/${encodeURIComponent(processId)}/substitution-analyst`, {
    task_ids: taskIds,
    raci_scope: raciScope,
    result,
  });
}

/** Analyst overrides (edited numbers/recommendation) on an existing draft. */
export async function updateSubstitutionDraft(processId, draftId, payload) {
  if (!processId || !draftId) throw new Error("processId and draftId are required");
  return api.patch(
    `/process/${encodeURIComponent(processId)}/substitution-analyst/${encodeURIComponent(draftId)}`,
    payload,
  );
}

/** Confirm a draft. Only this call is meant to affect anything downstream of the report. */
export async function confirmSubstitutionDraft(processId, draftId) {
  if (!processId || !draftId) throw new Error("processId and draftId are required");
  return api.patch(
    `/process/${encodeURIComponent(processId)}/substitution-analyst/${encodeURIComponent(draftId)}/confirm`,
    {},
  );
}
